import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { acceptInviteRequest, setAccessToken } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { useFeedback } from '../../hooks/useFeedback';
import { getApiErrorMessage } from '../../utils/apiError';
import '../LoginPage/LoginPage.css';
import './InvitePage.css';
import {
  getAppInviteHref,
  isMobileBrowser,
} from './openInApp';

function OpenInAppBanner({ token }: { token: string }) {
  if (!token || !isMobileBrowser()) return null;
  return (
    <>
      <a className="login-open-app" href={getAppInviteHref(token)}>
        Открыть в приложении
      </a>
      <p className="login-open-app-note">
        Если приложения нет — заполните форму на этой странице
      </p>
    </>
  );
}

const InvitePage = () => {
  const { token: tokenParam } = useParams();
  const tokenFromQuery = useMemo(() => {
    const q = new URLSearchParams(window.location.search).get('token');
    return tokenParam || q || '';
  }, [tokenParam]);

  const [form, setForm] = useState({
    name: '',
    login: '',
    password: '',
    token: tokenFromQuery,
  });
  const [busy, setBusy] = useState(false);
  const { showError, feedbackNode } = useFeedback();
  const { isAuthenticated, loading, user, logout, refreshClubs } = useAuth();

  useEffect(() => {
    const token = tokenFromQuery.trim();
    if (!token) return;
    setForm((prev) => ({ ...prev, token }));
  }, [tokenFromQuery]);

  const finishSession = async (session: { access_token?: string }) => {
    if (session.access_token) {
      setAccessToken(session.access_token);
    }
    try {
      await refreshClubs();
    } catch {
      // ignore
    }
    window.location.href = '/aircraft';
  };

  const handleJoinExisting = async () => {
    setBusy(true);
    try {
      const session = await acceptInviteRequest({
        token: form.token.trim(),
      });
      await finishSession(session);
    } catch (err) {
      showError(getApiErrorMessage(err, 'Не удалось принять приглашение'));
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
    } catch {
      // форма приглашения всё равно нужна
    }
    setBusy(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const session = await acceptInviteRequest({
        token: form.token.trim(),
        name: form.name.trim(),
        login: form.login.trim(),
        password: form.password,
      });
      await finishSession(session);
    } catch (err) {
      showError(getApiErrorMessage(err, 'Не удалось принять приглашение'));
      setBusy(false);
    }
  };

  if (loading) {
    return null;
  }

  if (isAuthenticated) {
    return (
      <div className="login-page">
        {feedbackNode}
        <div className="login-card">
          <img src="/plane.png" alt="" className="login-icon" />
          <h1>Приглашение</h1>
          <OpenInAppBanner token={tokenFromQuery} />
          <p className="login-subtitle">
            Вы вошли как <strong>{user?.name || user?.login}</strong>
            {user?.club_name ? (
              <>
                {' '}
                (клуб «{user.club_name}»). Можно вступить в другой клуб этим же
                аккаунтом или выйти и создать новый.
              </>
            ) : (
              <>.</>
            )}
          </p>
          <button
            type="button"
            className="login-primary"
            onClick={handleJoinExisting}
            disabled={busy}
          >
            {busy ? 'Вступление...' : 'Вступить этим аккаунтом'}
          </button>
          <button
            type="button"
            className="login-secondary"
            onClick={handleLogout}
            disabled={busy}
          >
            Выйти и зарегистрироваться заново
          </button>
          <p className="login-footer">
            <Link to="/">Остаться в текущем клубе</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {feedbackNode}
      <div className="login-card">
        <img src="/plane.png" alt="" className="login-icon" />
        <h1>Приглашение</h1>
        <p className="login-subtitle">Вступление в клуб</p>
        <OpenInAppBanner token={tokenFromQuery} />
        <form onSubmit={handleSubmit}>
          {!tokenParam && (
            <input
              type="text"
              placeholder="Токен приглашения"
              value={form.token}
              onChange={(e) => setForm({ ...form, token: e.target.value })}
              required
            />
          )}
          <input
            type="text"
            placeholder="Ваше имя"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Логин"
            value={form.login}
            onChange={(e) => setForm({ ...form, login: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Пароль"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={4}
          />
          <button type="submit" disabled={busy}>
            {busy ? 'Вступление...' : 'Присоединиться'}
          </button>
        </form>
        <p className="login-footer">
          <Link to="/login">Войти</Link>
        </p>
      </div>
    </div>
  );
};

export default InvitePage;
